import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { getAuth } from 'firebase/auth';
import cogoToast from 'cogo-toast';
import { addReview } from 'services/backendCalls';

const Overlay = styled.div`${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}`;
const Modal = styled.div`${tw`bg-white rounded-lg p-8 w-full max-w-md`}`;
const Title = tw.h2`text-2xl font-bold mb-6 text-gray-800`;
const Label = tw.label`block text-sm font-semibold text-gray-600 mb-1`;
const Textarea = tw.textarea`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-primary-500 h-24`;
const Select = tw.select`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-primary-500`;
const ButtonRow = tw.div`flex justify-end gap-3 mt-4`;
const SubmitBtn = tw.button`bg-primary-500 text-white px-5 py-2 rounded font-semibold hover:bg-primary-600 transition`;
const CancelBtn = tw.button`bg-gray-200 text-gray-700 px-5 py-2 rounded font-semibold hover:bg-gray-300 transition`;

export default function ReviewForm({ item, onClose }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    if (!auth.currentUser) {
      cogoToast.error('You must be logged in to leave a review.');
      return;
    }

    if (!comment.trim()) {
      cogoToast.error('Please enter a comment.');
      return;
    }

    setIsSubmitting(true);
    try {
      const reviewData = {
        userId: auth.currentUser.uid,
        userName: auth.currentUser.displayName || auth.currentUser.email,
        foodId: item.firestoreId || item.id,
        foodName: item.title || item.name,
        category: item.category || item.categoryName || 'Unknown Category',
        market: item.market || item.marketName || 'Unknown Market',
        rating: Number(rating),
        comment,
        status: 'pending', // Requires admin approval
        createdAt: new Date().toISOString()
      };

      await addReview(reviewData);
      cogoToast.success('Review submitted successfully. It will be visible after approval.');
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      cogoToast.error('Failed to submit review.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Title>Rate your experience</Title>
        <p tw="mb-4 text-sm text-gray-600">You are reviewing <strong>{item.title || item.name}</strong></p>
        
        <form onSubmit={handleSubmit}>
          <Label>Rating</Label>
          <Select value={rating} onChange={e => setRating(e.target.value)}>
            <option value={5}>5 - Excellent</option>
            <option value={4}>4 - Very Good</option>
            <option value={3}>3 - Average</option>
            <option value={2}>2 - Poor</option>
            <option value={1}>1 - Terrible</option>
          </Select>

          <Label>Review Comment</Label>
          <Textarea 
            placeholder="Tell us what you thought about this item..."
            value={comment}
            onChange={e => setComment(e.target.value)}
          />

          <ButtonRow>
            <CancelBtn type="button" onClick={onClose} disabled={isSubmitting}>Cancel</CancelBtn>
            <SubmitBtn type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </SubmitBtn>
          </ButtonRow>
        </form>
      </Modal>
    </Overlay>,
    document.body
  );
}
